import {Col, Row} from '@jetbrains/ring-ui/components/grid/grid'
import classNames from 'classnames'
import * as React from 'react'

import {useDerivedState} from '../../../../hocs/withDerivedState'
import {getBuild} from '../../../../selectors'
import type {AgentId, BuildId} from '../../../../types'
import BuildBranch from '../../../common/BranchLabel/BuildBranchLabel.container'
import BuildDuration from '../../../common/BuildDuration/BuildDuration.container'
import BuildNumberContainer from '../../../common/BuildNumber/BuildNumber.container'
import BuildPath from '../../../common/BuildPath/BuildPath.container'
import BuildStatus from '../../../common/BuildStatus/BuildStatus'
import Icon from '../../../common/Icon/Icon'
import ListItem from '../../../common/ListItem/ListItem'
import StopBuild from '../../../common/StopBuild/StopBuild.container'
import {useParentVisibility} from '../../../common/Visible/Visible.hooks'

import AgentLine from './AgentLine/AgentLine.container'
import AgentStatus from './AgentStatus/AgentStatus.container'
import AgentToggle from './AgentToggle/AgentToggle.container'
import AuthorizeAgent from './AuthorizeAgent/AuthorizeAgent.container'

import styles from './Agent.css'

import {useAppSelector} from 'src/hooks/react-redux'

type AgentBuildProps = {
  buildId: BuildId
}
export type AgentListItemProps = {
  className?: string
  agentId: AgentId
  withPool?: boolean
  isAuthorized?: boolean
  isActive?: boolean
  isIdle: boolean
  buildId: BuildId | null | undefined
  isFirst?: boolean
  ip?: string
  hideTopBorder?: boolean
  displayConnectionStatus?: boolean
}
type AgentProps = AgentListItemProps & {
  isVisible: boolean
}

function AgentBuild({buildId}: AgentBuildProps) {
  const isVisible = useParentVisibility() === true
  const hasBeenShown = useDerivedState(isVisible, state => state || isVisible)
  const buildTypeId = useAppSelector(state => getBuild(state, buildId)?.buildType)
  const buildState = useAppSelector(state => getBuild(state, buildId)?.state)
  return (
    <Row
      baseline="xs"
      className={classNames(styles.build, !isVisible && styles.buildPlaceholderHeight)}
    >
      {React.useMemo(
        () => (
          <Col className={styles.number}>
            <BuildNumberContainer buildId={buildId} />
          </Col>
        ),
        [buildId],
      )}
      {isVisible || hasBeenShown ? (
        <Col xs className={styles.buildInfo}>
          <div className={styles.type}>
            <BuildBranch className={styles.branch} buildId={buildId} />
            {buildTypeId != null && (
              <BuildPath
                buildId={buildId}
                isVisible={isVisible}
                leafBuildTypeId={buildTypeId}
                className={styles.buildPath}
                withCollapsing
              />
            )}
          </div>
          <div className={styles.statusLine}>
            <BuildStatus
              showDropdown
              buildId={buildId}
              buildTypeId={buildTypeId}
              className={styles.status}
              state={buildState}
            />
            <BuildDuration buildId={buildId} />
            <StopBuild buildId={buildId} className={styles.stop} />
          </div>
        </Col>
      ) : (
        <span className={styles.loadingPlaceholder}>{'Loading...'}</span>
      )}
    </Row>
  )
}

const AgentListItem = React.memo<AgentListItemProps>(function AgentListItem({
  className,
  agentId,
  withPool,
  isAuthorized,
  isActive,
  isIdle,
  buildId,
  isFirst,
  ip,
  hideTopBorder,
  displayConnectionStatus,
}) {
  const classes = classNames(className, {
    [styles.withToggle]: isAuthorized,
  })
  return (
    <ListItem className={classes} hideTopBorder={hideTopBorder}>
      {isAuthorized ? (
        <AgentToggle className={styles.toggle} agentId={agentId} />
      ) : (
        <Col className={styles.authorize}>
          <AuthorizeAgent agentId={agentId} className={styles.authButton} />
          {isAuthorized || (
            <div className={styles.ip} title="IP address">
              {ip}
            </div>
          )}
        </Col>
      )}
      <Col className={styles.agentStatus}>
        <AgentStatus agentId={agentId} withPool={withPool} />
      </Col>
      <Col xs className={styles.details}>
        {!isActive && (
          <AgentLine
            isFirst={isFirst}
            agentId={agentId}
            className={classNames({
              [styles.withBuild]: buildId != null,
            })}
            displayConnectionStatus={displayConnectionStatus}
          />
        )}
        {isActive && buildId == null && (
          <div className={styles.idle}>
            {!isIdle && <Icon className={styles.icon} name="running_green" />}
            {isIdle
              ? 'Idle'
              : 'Running a build. You do not have permissions to see the build details.'}
          </div>
        )}
        {buildId != null && <AgentBuild buildId={buildId} />}
      </Col>
    </ListItem>
  )
})

class Agent extends React.Component<AgentProps> {
  shouldComponentUpdate(nextProps: AgentProps): boolean {
    return !(!this.props.isVisible && !nextProps.isVisible)
  }

  render(): React.ReactNode {
    const {isVisible, ...restProps} = this.props
    return <AgentListItem {...restProps} />
  }
}

export default Agent
